import React, { useState, useEffect, useRef } from "react";
import {
  IconClock, IconPin, IconTag, IconX, IconLeaf,
  IconExternal, IconInfo, IconFlame, IconCheck, IconCart,
} from "./icons";
import { SOURCE_META, DEAL_TYPE_META } from "./seedData";
import { formatDistance } from "./location";

// ─── THEME (pure white app-wide; dark cards pop on white) ───
export const T = {
  bg: "#FFFFFF", bgCard: "#FFFFFF", bgCardAlt: "#F6F6F6",
  border: "#E8E8E8", borderStrong: "#D5D5D5",
  text: "#1C1915", textSub: "#6B6560", textMuted: "#9A9A9A",
  accent: "#1F7A4D", accentDark: "#16603C", accentLight: "#EAF5EE", accentBorder: "#BFE0CC",
  gold: "#B98A1D", goldBg: "#FBF3DC",
  red: "#D43A3A", redBg: "#FDEAEA",
  shadow: "0 1px 3px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)",
  shadowMd: "0 8px 32px rgba(0,0,0,0.14)",
};
export const SANS = "'DM Sans', -apple-system, sans-serif";
export const SERIF = "'DM Serif Display', Georgia, serif";

export const money = (n) =>
  "$" + Number(n).toFixed(2).replace(/\.00$/, "");

// ─── Haptics: tiered, guarded — light for rapid taps, heavier for state ──
// changes. navigator.vibrate is mobile-only; every call is guarded so
// desktop is a silent no-op.
export function buzz(pattern) {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate)
      navigator.vibrate(pattern);
  } catch { /* ignore */ }
}
export const buzzLight = () => buzz(8);            // tab switches, chips, pins
export const buzzMedium = () => buzz(25);          // confirmations, toggles
export const buzzHeavy = () => buzz([35, 40, 35]); // reserved: deal posting returns later

// ─── Count-up: numbers never jump — they ease from the previous value ───
export function useCountUp(target, duration = 800) {
  const [val, setVal] = useState(0);
  const valRef = useRef(0);
  useEffect(() => {
    const from = valRef.current;
    if (from === target) {
      setVal(target);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const e = 1 - Math.pow(1 - p, 3); // easeOutCubic
      const v = from + (target - from) * e;
      valRef.current = v;
      setVal(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

// ─── Countdown ──────────────────────────────────────────────────────────
export function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function countdownParts(endISO, now) {
  const ms = new Date(endISO).getTime() - now;
  if (ms <= 0) return null;
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

export function Countdown({ endISO, now }) {
  const parts = countdownParts(endISO, now);
  const urgent = parts && new Date(endISO).getTime() - now < 60 * 60000;
  if (!parts)
    return (
      <span style={{ ...styles.countPill, background: T.redBg, color: T.red }}>
        <IconClock size={13} /> Ended
      </span>
    );
  return (
    <span
      style={{
        ...styles.countPill,
        background: urgent ? T.redBg : T.goldBg,
        color: urgent ? T.red : T.gold,
      }}
    >
      <IconClock size={13} /> {parts} left
    </span>
  );
}

// ─── Rescue countdown pill (Mobbin: tasteful urgency) ────────────────────
// "Rescue before 9:30 PM" — urgency anchored to the pickup moment, formatted
// as a labeled pill, not a raw ticking number. Gold when comfortable, red
// under an hour out.
export function RescuePill({ endISO, now }) {
  const ms = new Date(endISO).getTime() - now;
  if (ms <= 0)
    return (
      <span style={{ ...styles.countPill, background: T.redBg, color: T.red }}>
        <IconClock size={13} /> Ended
      </span>
    );
  const urgent = ms < 60 * 60000;
  return (
    <span
      style={{
        ...styles.countPill,
        background: urgent ? T.redBg : T.goldBg,
        color: urgent ? T.red : T.gold,
      }}
    >
      <IconClock size={13} />
      <span className="tnum">Rescue before {fmtTime(endISO)}</span>
    </span>
  );
}

// ─── Dismissible promo strip (one at a time, respectful) ─────────────────
export function PromoStrip({ id, icon, text, onDismiss }) {
  return (
    <div style={styles.promo}>
      <span style={{ color: T.accentDark, display: "flex" }}>{icon}</span>
      <span style={styles.promoText}>{text}</span>
      <button onClick={onDismiss} style={styles.promoX} aria-label="Dismiss">
        <IconX size={14} />
      </button>
    </div>
  );
}

// ─── Empty state: custom illustration, never a dead end ──────────────────
export function EmptyDeals({ onGoToList }) {
  return (
    <div style={styles.emptyWrap}>
      <svg width="120" height="96" viewBox="0 0 120 96" fill="none" aria-hidden="true">
        <ellipse cx="60" cy="86" rx="34" ry="6" fill="#E5E0D8" />
        <path
          d="M34 44h52l-5 34a6 6 0 0 1-6 5H45a6 6 0 0 1-6-5l-5-34z"
          stroke="#1F7A4D" strokeWidth="3" strokeLinejoin="round"
        />
        <path d="M34 44c2-8 8-13 14-15M86 44c-2-8-8-13-14-15" stroke="#1F7A4D" strokeWidth="3" strokeLinecap="round" />
        <circle cx="60" cy="22" r="9" stroke="#B98A1D" strokeWidth="3" />
        <path d="M60 13v-4M60 35v-4M51 22h-4M73 22h-4" stroke="#B98A1D" strokeWidth="3" strokeLinecap="round" />
        <path d="M48 62c4-6 9-9 14-10M72 62c-4-6-9-9-14-10" stroke="#B98A1D" strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
      </svg>
      <div style={styles.emptyTitle}>No live closing-time deals right now</div>
      <div style={styles.emptySub}>
        These appear near closing time, when stores mark down what's left.
        Build your grocery list and we'll flag your items the moment they're
        on sale.
      </div>
      <button onClick={onGoToList} style={styles.emptyCta}>
        <IconCart size={16} /> Build my grocery list
      </button>
    </div>
  );
}

// ─── Source badge + link-out ────────────────────────────────────────────
export function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.crowdsourced;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "4px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
        background: meta.bg, color: meta.color, whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

// ─── Deal-type badge (surprise bag / flyer tip) ───────────────────────────
export function DealTypeBadge({ dealType }) {
  const meta = DEAL_TYPE_META[dealType];
  if (!meta) return null;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "4px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
        background: meta.bg, color: meta.color, whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

// ─── "Still there?" crowd confirmations ────────────────────────────────
// state: { count, latest, confirmed }. `confirmed` = this session already
// tapped it (guarded by localStorage in App). `count`/`latest` come from
// Supabase for remote deals, or session-local state for seeds/demo posts.
export function relTime(iso, now) {
  const mins = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function StillThere({ deal, state, now, onConfirm, large }) {
  const count = (state && state.count) || 0;
  const latest = state && state.latest;
  const confirmed = !!(state && state.confirmed);
  const base = large ? styles.confirmBtnLarge : styles.confirmPill;
  // v2.2: confirming gives a single soft celebratory pulse — 300ms,
  // scale 1→1.06→1. No confetti. The key swap remounts the label so the
  // pulse plays exactly once per confirm.
  const label = confirmed ? (
    <span key="done" style={{ display: "inline-flex", alignItems: "center", gap: 6, animation: "softPulse 0.3s cubic-bezier(0.22,1,0.36,1)" }}>
      <IconCheck size={large ? 16 : 13} />
      <span className="tnum">
        {count > 0 ? `${count} confirmed` : "Confirmed"}
      </span>
      {count > 0 && latest && (
        <span style={{ fontWeight: 500, opacity: 0.7 }}>· {relTime(latest, now)}</span>
      )}
    </span>
  ) : (
    <span key="todo" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <IconCheck size={large ? 16 : 13} />
      Still there?{count > 0 && <span className="tnum" style={{ opacity: 0.7 }}>· {count}</span>}
    </span>
  );
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); if (!confirmed) onConfirm(deal); }}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !confirmed) {
          e.preventDefault(); e.stopPropagation(); onConfirm(deal);
        }
      }}
      style={{ ...base, ...(confirmed ? styles.confirmDone : null), cursor: confirmed ? "default" : "pointer" }}
      aria-label={confirmed
        ? `You confirmed this deal is still available${count > 0 ? `, ${count} total` : ""}`
        : "Confirm this deal is still available at this store and price"}
    >
      {label}
    </span>
  );
}

// ─── Deal card ──────────────────────────────────────────────────────────
// distanceKm: number|null — straight-line km from the user (null = show area).
// poster: { display_name, alert_count }|null — trust line for signed-in posts.
// v2.1: the price is the single dominant element (huge, tight, black);
// cards ending within 2h flip to a dark high-contrast variant so the
// urgency reads against the light feed (Land-book dark/light bands).
export function DealCard({ deal, now, onOpen, confirmState, onConfirm, expired, distanceKm, poster }) {
  const pct = Math.round((1 - deal.deal_price / deal.original_price) * 100);
  const isExample = deal.id && String(deal.id).startsWith("seed-");
  const dist = formatDistance(distanceKm);
  const msLeft = new Date(deal.pickup_end).getTime() - now;
  const endingSoon = !expired && msLeft > 0 && msLeft < 2 * 3600 * 1000;
  const dk = endingSoon;
  return (
    <button
      onClick={() => onOpen(deal)}
      style={{ ...styles.card, ...(dk ? styles.cardDark : null), ...(expired ? { filter: "grayscale(0.85)", opacity: 0.75 } : null) }}
      aria-label={`${deal.title} at ${deal.store_name}${expired ? " (expired)" : ""}`}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
            <SourceBadge source={deal.source} />
            <DealTypeBadge dealType={deal.deal_type} />
            {expired ? <span style={styles.expiredBadge}>Expired</span> : <span style={dk ? styles.pctDark : styles.pct}>-{pct}%</span>}
            {dist && (
              <span style={dk ? styles.distChipDark : styles.distChip}>
                <IconPin size={11} /> <span className="tnum">{dist}</span>
              </span>
            )}
          </div>
          <div style={{ ...styles.cardTitle, ...(dk ? { color: "#fff" } : null) }}>{deal.title}</div>
          <div style={{ ...styles.cardStore, ...(dk ? { color: "rgba(255,255,255,0.7)" } : null) }}>
            <IconPin size={13} /> {deal.store_name} · {deal.area}
          </div>
          {poster && (
            <div style={{ ...styles.trustLine, ...(dk ? { color: "rgba(255,255,255,0.55)" } : null) }}>
              Posted by {poster.display_name}
              {poster.alert_count > 0 && (
                <span className="tnum"> · {poster.alert_count} alert{poster.alert_count === 1 ? "" : "s"}</span>
              )}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div className="tnum" style={{ ...styles.price, ...(dk ? { color: "#fff" } : null) }}>{money(deal.deal_price)}</div>
          <div className="tnum" style={{ ...styles.wasPrice, ...(dk ? { color: "rgba(255,255,255,0.5)" } : null) }}>{money(deal.original_price)}</div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <RescuePill endISO={deal.pickup_end} now={now} />
        <span style={{ ...styles.pickupLine, ...(dk ? { color: "rgba(255,255,255,0.75)" } : null) }} className="tnum">
          Save {money(deal.original_price - deal.deal_price)}
        </span>
      </div>
      <div style={{ marginTop: 10 }}>
        <StillThere deal={deal} state={confirmState} now={now} onConfirm={onConfirm} />
      </div>
      {isExample && (
        <div style={{ ...styles.exampleNote, ...(dk ? { color: "rgba(255,255,255,0.5)" } : null) }}>Example — check the app for today's live deal</div>
      )}
    </button>
  );
}

export function fmtTime(iso) {
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

// ─── Bottom sheet (vesttrack quick-add style) ───────────────────────────
// maxHeight caps the sheet (map pin sheets pass "60vh" so the map stays
// visible — Mobbin bottom-sheet grammar).
export function BottomSheet({ open, onClose, title, children, maxHeight }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setVisible(true), 20);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [open ]);
  if (!open) return null;
  return (
    <div style={styles.sheetOverlay} onClick={onClose}>
      <div
        style={{ ...styles.sheet, maxHeight: maxHeight || "88vh", transform: visible ? "translateY(0)" : "translateY(100%)" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={styles.sheetHandle} />
        <div style={styles.sheetHead}>
          <div style={styles.sheetTitle}>{title}</div>
          <button onClick={onClose} style={styles.iconBtn} aria-label="Close">
            <IconX size={20} />
          </button>
        </div>
        <div style={styles.sheetBody}>{children}</div>
      </div>
    </div>
  );
}

// ─── Deal detail ────────────────────────────────────────────────────────
export function DealDetail({ deal, now, onClose, onLinkOut, confirmState, onConfirm, distanceKm, poster }) {
  if (!deal) return null;
  const saved = deal.original_price - deal.deal_price;
  const pct = Math.round((1 - deal.deal_price / deal.original_price) * 100);
  const dist = formatDistance(distanceKm);
  return (
    <BottomSheet open={!!deal} onClose={onClose} title="Deal details">
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <SourceBadge source={deal.source} />
        <DealTypeBadge dealType={deal.deal_type} />
        <span style={styles.pct}>-{pct}%</span>
        {dist && (
          <span style={styles.distChip}>
            <IconPin size={11} /> <span className="tnum">{dist} away</span>
          </span>
        )}
      </div>
      <div style={{ ...styles.cardTitle, fontSize: 21, marginBottom: 4 }}>{deal.title}</div>
      <div style={{ ...styles.cardStore, marginBottom: 4 }}>
        <IconPin size={14} /> {deal.store_name} · {deal.area}
      </div>
      {poster && (
        <div style={{ ...styles.trustLine, fontSize: 13, marginBottom: 12 }}>
          Posted by {poster.display_name}
          {poster.alert_count > 0 && (
            <span className="tnum"> · {poster.alert_count} alert{poster.alert_count === 1 ? "" : "s"}</span>
          )}
        </div>
      )}
      {(!poster) && <div style={{ marginBottom: 12 }} />}
      <div style={styles.detailGrid}>
        <DetailRow icon={<IconTag size={16} />} label="Deal price" value={money(deal.deal_price)} strong />
        <DetailRow icon={<IconTag size={16} />} label="Regular price" value={money(deal.original_price)} />
        <DetailRow icon={<IconLeaf size={16} />} label="You save" value={money(saved)} strong accent />
        <DetailRow icon={<IconClock size={16} />} label="Pickup window" value={`${fmtTime(deal.pickup_start)} – ${fmtTime(deal.pickup_end)}`} />
      </div>
      {deal.note && (
        <div style={styles.noteBox}>
          <IconInfo size={14} /> <span>{deal.note}</span>
        </div>
      )}
      {deal.source_url && (
        <button onClick={() => onLinkOut(deal)} style={styles.linkBtn}>
          Open in {deal.source === "flashfood" ? "Flashfood" : deal.source === "tgtg" ? "Too Good To Go" : "browser"}
          <span className="tnum" style={{ opacity: 0.85, fontWeight: 600 }}>· save {money(saved)}</span>
          <IconExternal size={16} />
        </button>
      )}
      <StillThere deal={deal} state={confirmState} now={now} onConfirm={onConfirm} large />
      <div style={styles.finePrint}>
        Prices and availability change fast — confirm in the source app before heading out.
      </div>
    </BottomSheet>
  );
}

function DetailRow({ icon, label, value, strong, accent }) {
  return (
    <div style={styles.detailRow}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, color: T.textSub, fontSize: 13.5 }}>
        <span style={{ color: accent ? T.accent : T.textMuted }}>{icon}</span> {label}
      </span>
      <span
        className="tnum"
        style={{ fontWeight: strong ? 800 : 500, fontSize: strong ? 19 : 14, letterSpacing: strong ? -0.5 : 0, color: accent ? T.accent : T.text }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Last-call band: dark statement block mid-feed (Land-book contrast) ─
// Massive countdown type to the soonest-ending deal — real data only,
// ticking with the same `now` as the rest of the feed.
export function LastCallBand({ deal, now }) {
  if (!deal) return null;
  const parts = countdownParts(deal.pickup_end, now);
  if (!parts) return null;
  return (
    <div style={styles.lastCall}>
      <div style={styles.lastCallEyebrow}>Closing soonest</div>
      <div className="tnum" style={styles.lastCallTime}>{parts}</div>
      <div style={styles.lastCallSub}>{deal.store_name} · {deal.title}</div>
    </div>
  );
}

// ─── Sponsored slot (monetization-ready placeholder) ────────────────────
export function SponsoredSlot() {
  return (
    <div style={styles.sponsored}>
      <span style={styles.sponsoredTag}>Sponsored</span>
      <div style={styles.sponsoredText}>
        Your store here — reach Calgary food-rescuers at closing time.
      </div>
    </div>
  );
}

// ─── Impact bar ─────────────────────────────────────────────────────────
// v2.1: dark high-contrast card, HUGE tabular figures that count up — the
// numbers dominate, like Revolut's dark plan card.
export function ImpactBar({ lbs, saved }) {
  const lbsAnim = useCountUp(lbs);
  const savedAnim = useCountUp(saved);
  return (
    <div style={styles.impact}>
      <div style={styles.impactItem}>
        <span style={{ color: "#fff", display: "flex" }}><IconLeaf size={24} /></span>
        <div>
          <div className="tnum" style={styles.impactNum}>
            {lbsAnim.toFixed(1)}<span style={styles.impactUnit}> lbs</span>
          </div>
          <div style={styles.impactLabel}>food rescued</div>
        </div>
      </div>
      <div style={styles.impactDiv} />
      <div style={styles.impactItem}>
        <span style={{ color: "#fff", display: "flex" }}><IconFlame size={24} /></span>
        <div>
          <div className="tnum" style={styles.impactNum}>{money(savedAnim)}</div>
          <div style={styles.impactLabel}>saved vs regular</div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────
const styles = {
  countPill: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 14px", borderRadius: 20, fontSize: 13.5, fontWeight: 800,
  },
  // ── Dismissible promo strip (one at a time) ──
  promo: {
    display: "flex", alignItems: "center", gap: 10,
    background: T.goldBg, border: "1px solid #EBD9A0", borderRadius: 14,
    padding: "11px 12px", marginBottom: 12,
    animation: "fadein 0.3s ease",
  },
  promoText: { flex: 1, fontSize: 12.5, lineHeight: 1.45, color: T.textSub },
  promoX: {
    background: "transparent", border: "none", cursor: "pointer",
    color: T.textMuted, display: "flex", padding: 4, flexShrink: 0,
  },
  // ── Empty state ──
  emptyWrap: {
    textAlign: "center", padding: "36px 24px",
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18,
    animation: "fadein 0.3s ease",
  },
  emptyTitle: { fontSize: 17, fontWeight: 700, marginTop: 12 },
  emptySub: { fontSize: 13.5, color: T.textSub, marginTop: 6, lineHeight: 1.55 },
  emptyCta: {
    display: "inline-flex", alignItems: "center", gap: 8,
    marginTop: 18, padding: "14px 26px", borderRadius: 999, border: "none",
    background: "#141210", color: "#fff", fontSize: 14.5, fontWeight: 800,
    letterSpacing: -0.2, cursor: "pointer", fontFamily: "inherit",
    boxShadow: "0 8px 22px rgba(0,0,0,0.28)",
  },
  card: {
    display: "block", width: "100%", textAlign: "left",
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18,
    padding: 18, boxShadow: T.shadow, cursor: "pointer", fontFamily: "inherit",
    color: T.text, marginBottom: 12,
  },
  // v2.1: ending-soon dark variant — urgency as a dark/light contrast band.
  cardDark: {
    background: "#171310", borderColor: "#171310",
    boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
  },
  distChipDark: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
    background: "rgba(255,255,255,0.14)", color: "#fff", whiteSpace: "nowrap",
  },
  pctDark: {
    display: "inline-flex", padding: "5px 11px", borderRadius: 20,
    background: T.gold, color: "#fff", fontSize: 12, fontWeight: 800,
  },
  cardTitle: { fontSize: 16.5, fontWeight: 700, lineHeight: 1.3, marginBottom: 4 },
  cardStore: { display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: T.textSub },
  trustLine: { fontSize: 12, color: T.textMuted, marginTop: 5 },
  distChip: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "4px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
    background: "#EAF5EE", color: T.accentDark, border: `1px solid ${T.accentBorder}`,
    whiteSpace: "nowrap",
  },
  price: { fontSize: 32, fontWeight: 800, color: T.text, letterSpacing: -0.5, lineHeight: 1.05 },
  wasPrice: { fontSize: 13, color: T.textMuted, textDecoration: "line-through" },
  pct: {
    display: "inline-flex", padding: "5px 11px", borderRadius: 20,
    background: T.accentLight, color: T.accentDark, fontSize: 12, fontWeight: 800,
    border: `1px solid ${T.accentBorder}`,
  },
  expiredBadge: {
    display: "inline-flex", padding: "4px 9px", borderRadius: 20,
    background: T.red, color: "#fff", fontSize: 11.5, fontWeight: 700,
  },
  pickupLine: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: T.textSub },
  exampleNote: { marginTop: 10, fontSize: 11.5, color: T.textMuted, fontStyle: "italic" },
  confirmPill: {
    display: "inline-flex", alignItems: "center", padding: "7px 13px", borderRadius: 20,
    border: `1.5px solid ${T.accentBorder}`, background: "#fff", color: T.accentDark,
    fontSize: 12.5, fontWeight: 700, userSelect: "none",
  },
  confirmDone: { background: T.accentLight, border: `1.5px solid ${T.accent}` },
  confirmBtnLarge: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: "100%", padding: 14, borderRadius: 13, border: `1.5px solid ${T.accentBorder}`,
    background: "#fff", color: T.accentDark, fontSize: 14.5, fontWeight: 700,
    marginTop: 12, userSelect: "none",
  },
  sheetOverlay: {
    position: "fixed", inset: 0, background: "rgba(28,25,21,0.45)", zIndex: 50,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
    animation: "fadein 0.22s ease",
  },
  sheet: {
    background: T.bgCard, borderRadius: "22px 22px 0 0", width: "100%", maxWidth: 640,
    maxHeight: "88vh", display: "flex", flexDirection: "column",
    transition: "transform 0.34s cubic-bezier(0.22,1,0.36,1)",
    boxShadow: T.shadowMd,
  },
  sheetHandle: { width: 44, height: 5, borderRadius: 3, background: T.borderStrong, margin: "10px auto 0" },
  sheetHead: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px 4px" },
  sheetTitle: { fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 23, letterSpacing: -0.5 },
  iconBtn: { background: T.bgCardAlt, border: `1px solid ${T.border}`, borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.textSub },
  sheetBody: { padding: "12px 20px 28px", overflowY: "auto" },
  detailGrid: { background: T.bgCardAlt, border: `1px solid ${T.border}`, borderRadius: 14, padding: "6px 14px", marginBottom: 14 },
  detailRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.border}` },
  noteBox: { display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: T.textSub, background: T.goldBg, border: "1px solid #EBD9A0", borderRadius: 12, padding: "10px 12px", marginBottom: 14, lineHeight: 1.45 },
  linkBtn: {
    width: "100%", padding: 16, borderRadius: 999, border: "none", cursor: "pointer",
    background: "#141210", color: "#fff", fontSize: 15.5, fontWeight: 800,
    letterSpacing: -0.2, fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10,
  },
  finePrint: { fontSize: 11.5, color: T.textMuted, textAlign: "center", lineHeight: 1.5 },
  sponsored: {
    border: `1.5px dashed ${T.borderStrong}`, borderRadius: 18, padding: "18px 16px",
    textAlign: "center", marginBottom: 12, background: T.bgCardAlt,
  },
  // ── v2.1: Last-call dark statement band ──
  lastCall: {
    background: "#141210", borderRadius: 20, padding: "22px 20px",
    marginBottom: 12, boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
  },
  lastCallEyebrow: {
    fontSize: 10.5, fontWeight: 800, textTransform: "uppercase",
    letterSpacing: 2, color: T.gold,
  },
  lastCallTime: {
    fontSize: 46, fontWeight: 800, letterSpacing: -2,
    color: "#fff", lineHeight: 1.05, marginTop: 6,
  },
  lastCallSub: { fontSize: 13, color: "rgba(255,255,255,0.65)", marginTop: 8 },
  sponsoredTag: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1.5, color: T.textMuted, fontWeight: 700 },
  sponsoredText: { fontSize: 13.5, color: T.textSub, marginTop: 6, lineHeight: 1.5 },
  impact: {
    display: "flex", alignItems: "center", background: "#141210", borderRadius: 20,
    padding: "20px 20px", marginBottom: 18, color: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
  },
  impactItem: { display: "flex", alignItems: "center", gap: 13, flex: 1 },
  impactDiv: { width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.22)", margin: "0 14px" },
  impactNum: { fontSize: 38, fontWeight: 800, letterSpacing: -1.5, color: "#fff", lineHeight: 1 },
  impactUnit: { fontSize: 15, fontWeight: 700, letterSpacing: 0, color: "rgba(255,255,255,0.6)" },
  impactLabel: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: 1.4, color: "rgba(255,255,255,0.6)", marginTop: 7,
  },
};
